import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp, FaSlidersH } from 'react-icons/fa';
import { Box, Button, Grid, HStack } from '@tq/ui-components';

import { useConsultations } from 'api';

import { SearchText } from 'components';
import { useIsMobile, useThemedValueSwitch } from '@tq/ui-components/dist/hooks';
import { ConsultationShow, ConsultationSort, ConsultationViewType, FilterModal } from '..';
import { searchTextState } from '../../../../store';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

const DESKTOP_TEMPLATE = `
"view show search sort"
 / 262px 162px auto 230px
 `;

const MOBILE_TEMPLATE = `
"search search"
"filter optionsButton"
 / 1fr 1fr
`;

const MOBILE_TEMPLATE_OPTIONS = `
"search search"
"filter optionsButton"
"show_sort show_sort"
 / 1fr 1fr
`;

export const TopFilter: React.FC = () => {
  const { t } = useTranslation('search_filter');
  const { data: consultationsData } = useConsultations();

  const isMobile = useIsMobile();

  const [areOptionsVisible, setAreOptionsVisible] = useState(false);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const { isTQ } = useThemedValueSwitch();

  let gridDominantSecondarySurface = {
    borderColor: COLOURS.gray[400],
    borderWidth: 1,
    borderRadius: 5,
    backgroundColor: COLOURS.gray[200],
  };

  if (isTQ) {
    gridDominantSecondarySurface = {
      borderColor: 'grey.300',
      borderWidth: 1,
      borderRadius: 3,
      backgroundColor: 'grey.50',
    };
  }

  return (
    <Grid
      gridGap={{ base: 4, lg: 12 }}
      pt={6}
      pb={7}
      px={5}
      mx={{ base: -5, md: 0 }}
      justifyContent={{ base: 'center', lg: 'unset' }}
      gridTemplate={{
        base: areOptionsVisible ? MOBILE_TEMPLATE_OPTIONS : MOBILE_TEMPLATE,
        lg: DESKTOP_TEMPLATE,
      }}
      borderColor={gridDominantSecondarySurface.borderColor}
      borderWidth={gridDominantSecondarySurface.borderWidth}
      borderRadius={gridDominantSecondarySurface.borderRadius}
      backgroundColor={gridDominantSecondarySurface.backgroundColor}
    >
      {isMobile ? (
        <>
          <Box gridArea='search'>
            <SearchText
              searchState={searchTextState}
              placeholderText={t('placeholder_consultations', 'Search consultations')}
              recordCount={consultationsData?.consultations?.items?.length ?? 0}
              totalCount={consultationsData?.paginationInfo?.totalCount ?? 0}
            />
          </Box>
          <Button
            gridArea='filter'
            minWidth='unset'
            onClick={() => setIsFilterModalVisible(true)}
            rightIcon={<FaSlidersH />}
          >
            {t('filter', 'Filter')}
          </Button>
          <Button
            gridArea='optionsButton'
            variant='tertiary'
            minWidth='auto'
            rightIcon={areOptionsVisible ? <FaChevronUp /> : <FaChevronDown />}
            onClick={() => setAreOptionsVisible((v) => !v)}
          >
            {areOptionsVisible
              ? t('hide_options', 'Hide options')
              : t('show_options', 'Show options')}
          </Button>
          {areOptionsVisible && (
            <HStack spacing={2} gridArea='show_sort'>
              <ConsultationShow w='50%' flex='0 1 auto' />
              <ConsultationSort w='50%' flex='0 1 auto' />
            </HStack>
          )}
        </>
      ) : (
        <>
          <ConsultationViewType gridArea='view' />
          <ConsultationShow gridArea='show' />
          <Box gridArea='search' mt={9}>
            <SearchText
              searchState={searchTextState}
              placeholderText={t('placeholder_consultations', 'Search consultations')}
              recordCount={consultationsData?.consultations?.items?.length ?? 0}
              totalCount={consultationsData?.paginationInfo?.totalCount ?? 0}
            />
          </Box>
          <ConsultationSort gridArea='sort' />
        </>
      )}

      <FilterModal
        isOpen={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible((v) => !v)}
      />
    </Grid>
  );
};
